import { z } from 'zod';

/**
 * Platform where the purchase was made
 */
export type Platform = 'ios' | 'android' | 'web';

/**
 * Membership types supported by TractorPal
 */
export type MembershipType = 'subscription' | 'one-time' | 'none';

/**
 * Product IDs from RevenueCat / In-App Purchases
 */
export const InAppPurchaseCode = {
  ONE_TIME_MEMBERSHIP: 'com.live.tractorpal2.fullmember',
  SUBSCRIPTION_MEMBERSHIP: 'com.live.tractorpal2.fullmember.subscription',
} as const;

/**
 * Zod schema for Membership with safe fallbacks
 * Uses .catch() to provide default values on validation failure
 *
 * Stored in Firestore at: users/{uid}/membership
 * Written by Cloud Functions only (client read-only)
 */
export const MembershipSchema = z.object({
  /**
   * Type of membership
   * - 'subscription': Recurring subscription (has expiration date)
   * - 'one-time': Lifetime purchase (no expiration)
   * - 'none': No active membership
   */
  type: z.enum(['subscription', 'one-time', 'none']).catch('none'),

  /**
   * Product identifier from the app store
   * Examples: 'com.live.tractorpal2.fullmember.subscription'
   */
  productId: z.string().nullable().catch(null),

  /**
   * Expiration date for subscriptions (ISO 8601 date string)
   * null for one-time purchases and 'none' type
   */
  expirationDate: z.string().nullable().catch(null),

  /**
   * Original purchase date (ISO 8601 date string)
   */
  purchaseDate: z.string().nullable().catch(null),

  /**
   * Platform where the purchase was made
   */
  platform: z.enum(['ios', 'android', 'web']).nullable().catch(null),

  /**
   * Whether the membership is currently active
   * - true: User has active membership (can add unlimited machines)
   * - false: User has no membership (limited to 1 machine)
   */
  isActive: z.boolean().catch(false),
}).catch({
  type: 'none',
  productId: null,
  expirationDate: null,
  purchaseDate: null,
  platform: null,
  isActive: false,
});

/**
 * Membership type inferred from schema
 */
export type Membership = z.infer<typeof MembershipSchema>;

/**
 * Create default Membership object (no active membership)
 */
export function createDefaultMembership(): Membership {
  return {
    type: 'none',
    productId: null,
    expirationDate: null,
    purchaseDate: null,
    platform: null,
    isActive: false,
  };
}

/**
 * Check if membership is active and not expired
 * @param membership Membership object to check
 * @returns true if membership is active and valid
 */
export function isMembershipActive(membership: Membership): boolean {
  if (!membership.isActive) {
    return false;
  }

  // One-time purchases never expire
  if (membership.type === 'one-time') {
    return true;
  }

  // Check subscription expiration
  if (membership.type === 'subscription' && membership.expirationDate) {
    const expirationDate = new Date(membership.expirationDate);
    const now = new Date();
    return now < expirationDate;
  }

  return false;
}
